/**
 * BeforeAfterPhotoUpload.js - Before/After photo comparison for job completion
 * Allows workers to document their work quality with side-by-side photos
 */
import React, { useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Camera, X, ArrowRight, Loader2, Image, Check, 
  AlertCircle, Eye, Download, Maximize2
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import api from '../../services/api';

// PhotoSection component - defined OUTSIDE to prevent remount issues
const PhotoSection = ({ 
  type, 
  photos, 
  uploading, 
  onPhotoSelect, 
  onRemovePhoto, 
  onViewPhoto,
  readOnly 
}) => {
  const inputRef = useRef(null);
  const isAfter = type === 'after';
  
  // Use static color classes instead of dynamic interpolation
  const colorClasses = isAfter ? {
    badge: 'bg-green-500',
    icon: 'text-green-500',
    iconBg: 'bg-green-500/20',
    border: 'border-green-500/30 hover:border-green-500'
  } : {
    badge: 'bg-orange-500',
    icon: 'text-orange-500',
    iconBg: 'bg-orange-500/20',
    border: 'border-orange-500/30 hover:border-orange-500'
  };
  
  const Icon = isAfter ? Check : Camera;
  
  return (
    <div className="flex-1 space-y-3">
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-full ${colorClasses.iconBg} flex items-center justify-center`}>
          <Icon className={`w-4 h-4 ${colorClasses.icon}`} />
        </div>
        <h4 className="font-semibold capitalize">{type} Photos</h4>
        <span className="text-xs text-[var(--text-muted)]">({photos.length})</span>
      </div>
      
      {/* Photo grid */}
      <div className="grid grid-cols-2 gap-2">
        {photos.map((photo) => (
          <div
            key={photo.id}
            className="relative aspect-square rounded-lg overflow-hidden bg-[var(--background-subtle)] group"
          >
            <img
              src={photo.url}
              alt={`${type} photo`}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onViewPhoto(photo);
                }}
                className="p-2 bg-white/80 rounded-full hover:bg-white"
              >
                <Eye className="w-4 h-4 text-black" />
              </button>
              {!readOnly && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onRemovePhoto(photo.id, type);
                  }}
                  className="p-2 bg-red-500/80 rounded-full hover:bg-red-500"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              )}
            </div>
            <span className={`absolute top-1 left-1 text-[10px] px-1.5 py-0.5 rounded ${colorClasses.badge} text-white font-medium`}>
              {type.toUpperCase()}
            </span>
          </div>
        ))}
        
        {/* Add photo button */}
        {!readOnly && (
          <>
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              onChange={(e) => onPhotoSelect(e, type)}
              onClick={(e) => e.stopPropagation()}
              className="hidden"
              data-testid={`${type}-photo-input`}
            />
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                inputRef.current?.click();
              }}
              disabled={uploading}
              className={`aspect-square rounded-lg border-2 border-dashed ${colorClasses.border} flex flex-col items-center justify-center gap-1 transition-colors`}
            >
              {uploading ? (
                <Loader2 className="w-6 h-6 animate-spin text-[var(--text-muted)]" />
              ) : (
                <>
                  <Camera className={`w-6 h-6 ${colorClasses.icon}`} />
                  <span className="text-xs text-[var(--text-muted)]">Add</span>
                </>
              )}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const BeforeAfterPhotoUpload = ({
  bookingId,
  serviceType = 'cleaning',
  onComplete,
  initialPhotos = { before: [], after: [] },
  readOnly = false,
  showComparison = true
}) => {
  const [beforePhotos, setBeforePhotos] = useState(initialPhotos.before || []);
  const [afterPhotos, setAfterPhotos] = useState(initialPhotos.after || []);
  const [uploadingBefore, setUploadingBefore] = useState(false);
  const [uploadingAfter, setUploadingAfter] = useState(false);
  const [selectedComparison, setSelectedComparison] = useState(null);
  const [viewingPhoto, setViewingPhoto] = useState(null);

  // Handle photo capture/selection
  const handlePhotoSelect = useCallback(async (event, type) => {
    event.preventDefault();
    event.stopPropagation();
    
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    
    const setUploading = type === 'before' ? setUploadingBefore : setUploadingAfter;
    const setPhotos = type === 'before' ? setBeforePhotos : setAfterPhotos;
    
    setUploading(true);
    
    for (const file of files) {
      try {
        // Validate file
        if (!file.type.startsWith('image/')) {
          toast.error('Please select an image file');
          continue;
        }
        
        if (file.size > 10 * 1024 * 1024) {
          toast.error('Image must be smaller than 10MB');
          continue;
        }
        
        const formData = new FormData();
        formData.append('file', file);
        formData.append('booking_id', bookingId);
        formData.append('photo_type', type);
        
        const response = await api.post('/job-workflow/photos/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        const photoData = {
          id: response.data.photo_id || Date.now().toString(),
          url: response.data.url || URL.createObjectURL(file),
          type: response.data.type || type,
          uploaded_at: response.data.uploaded_at || new Date().toISOString(),
          filename: file.name
        };
        
        setPhotos(prev => [...prev, photoData]);
        toast.success(`${type === 'before' ? 'Before' : 'After'} photo uploaded`);
      } catch (error) {
        console.error('Upload failed:', error);
        toast.error(`Failed to upload ${type} photo`);
      }
    }
    
    setUploading(false);
    
    // Reset input
    event.target.value = '';
  }, [bookingId]);

  // Remove a photo
  const removePhoto = useCallback(async (photoId, type) => {
    try {
      await api.delete(`/job-workflow/photos/${photoId}`);
      
      const setPhotos = type === 'before' ? setBeforePhotos : setAfterPhotos;
      setPhotos(prev => prev.filter(p => p.id !== photoId));
      
      toast.success('Photo removed');
    } catch (error) {
      toast.error('Failed to remove photo');
    }
  }, []);

  // Handle completion
  const handleComplete = useCallback(() => {
    if (onComplete) {
      onComplete({
        before: beforePhotos,
        after: afterPhotos
      });
    }
  }, [beforePhotos, afterPhotos, onComplete]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold flex items-center gap-2">
            <Image className="w-5 h-5 text-[var(--primary)]" />
            Before & After Photos
          </h3>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            Document the transformation of your work
          </p>
        </div>
        {!readOnly && (beforePhotos.length > 0 || afterPhotos.length > 0) && (
          <span className="text-sm font-medium text-[var(--primary)]">
            {beforePhotos.length + afterPhotos.length} photos
          </span>
        )}
      </div>

      {/* Instructions */}
      {!readOnly && beforePhotos.length === 0 && afterPhotos.length === 0 && (
        <div className="p-4 bg-[var(--primary)]/10 border border-[var(--primary)]/20 rounded-xl">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-[var(--primary)] flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-[var(--primary)]">Capture Quality Proof</p>
              <ul className="list-disc list-inside mt-1 text-[var(--text-secondary)] space-y-1">
                <li>Take <strong>Before</strong> photos when you arrive</li>
                <li>Take <strong>After</strong> photos when you finish</li>
                <li>Photos help customers see the value of your work</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Side-by-side upload sections */}
      <div className="flex gap-4">
        <PhotoSection
          type="before"
          photos={beforePhotos}
          uploading={uploadingBefore}
          onPhotoSelect={handlePhotoSelect}
          onRemovePhoto={removePhoto}
          onViewPhoto={setViewingPhoto}
          readOnly={readOnly}
        />
        
        {/* Arrow between sections */}
        <div className="flex flex-col items-center justify-center">
          <ArrowRight className="w-6 h-6 text-[var(--text-muted)]" />
        </div>
        
        <PhotoSection
          type="after"
          photos={afterPhotos}
          uploading={uploadingAfter}
          onPhotoSelect={handlePhotoSelect}
          onRemovePhoto={removePhoto}
          onViewPhoto={setViewingPhoto}
          readOnly={readOnly}
        />
      </div>

      {/* Comparison View */}
      {showComparison && beforePhotos.length > 0 && afterPhotos.length > 0 && (
        <div className="space-y-3">
          <h4 className="font-semibold text-sm">Compare Results</h4>
          <div className="grid grid-cols-2 gap-3">
            {beforePhotos.slice(0, Math.min(beforePhotos.length, afterPhotos.length)).map((beforePhoto, idx) => {
              const afterPhoto = afterPhotos[idx];
              if (!afterPhoto) return null;
              
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSelectedComparison({ before: beforePhoto, after: afterPhoto });
                  }}
                  className="relative aspect-video rounded-lg overflow-hidden bg-[var(--background-subtle)] border border-[var(--border-primary)] hover:border-[var(--primary)] transition-colors"
                >
                  {/* Split view preview */}
                  <div className="absolute inset-0 flex">
                    <div className="w-1/2 overflow-hidden">
                      <img
                        src={beforePhoto.url}
                        alt="Before"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="w-1/2 overflow-hidden border-l-2 border-white">
                      <img
                        src={afterPhoto.url}
                        alt="After"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 hover:opacity-100 transition-opacity">
                    <Maximize2 className="w-6 h-6 text-white" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Complete button */}
      {!readOnly && onComplete && (
        <Button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleComplete();
          }}
          className="w-full bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-black"
        >
          Continue
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      )}

      {/* Full photo viewer modal */}
      <AnimatePresence>
        {viewingPhoto && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={() => setViewingPhoto(null)}
          >
            <button
              type="button"
              onClick={() => setViewingPhoto(null)}
              className="absolute top-4 right-4 p-2 bg-white/20 rounded-full hover:bg-white/30"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            <img
              src={viewingPhoto.url}
              alt="Full view"
              className="max-w-full max-h-full object-contain rounded-lg"
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Comparison modal */}
      <AnimatePresence>
        {selectedComparison && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 z-50 p-4"
            onClick={() => setSelectedComparison(null)}
          >
            <button
              type="button"
              onClick={() => setSelectedComparison(null)}
              className="absolute top-4 right-4 p-2 bg-white/20 rounded-full hover:bg-white/30 z-10"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            
            <div className="h-full flex flex-col items-center justify-center gap-4">
              <h3 className="text-white text-lg font-semibold">Before & After Comparison</h3>
              
              <div className="flex gap-4 max-w-4xl w-full">
                <div className="flex-1 space-y-2">
                  <span className="text-orange-400 text-sm font-medium">BEFORE</span>
                  <img
                    src={selectedComparison.before.url}
                    alt="Before"
                    className="w-full rounded-lg object-contain max-h-[60vh]"
                  />
                </div>
                <div className="flex-1 space-y-2">
                  <span className="text-green-400 text-sm font-medium">AFTER</span>
                  <img
                    src={selectedComparison.after.url}
                    alt="After"
                    className="w-full rounded-lg object-contain max-h-[60vh]"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BeforeAfterPhotoUpload;
